import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Text,
  Image,
  Spinner,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { isRouteExist } from "@root/utils/helpers";
import useSidebar from "@root/hooks/useSidebar";
import { useOrganizationBranding } from "@root/context/organization-branding-context";
import ArrowLeftIcon from "@root/assets/icons/ArrowLeftIcon.svg";
import { ROUTE_NAMES } from "@root/constants";
import { useNavigate } from "react-router-dom";
import { CustomButton } from "@root/components/common/button/customButton";

interface GuestMobileSideMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export function GuestMobileSideMenu({
  isOpen,
  onClose,
}: GuestMobileSideMenuProps) {
  const { t } = useTranslation("common");
  const { logoUrl, defaultLogoUrl, loading } = useOrganizationBranding();
  const navigate = useNavigate();

  const { sidebar, pathname } = useSidebar();

  const handleMenuClick = (menu: any) => {
    menu?.onClick();
    onClose();
  };

  const handleNavigate = (route: string) => {
    navigate(route);
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="full">
      <DrawerOverlay />
      <DrawerContent
        bgColor={"theme.bg.box"}
        maxW="90%"
        style={{ height: "100dvh", maxHeight: "100dvh" }}
      >
        <DrawerHeader borderBottomWidth="1px" pb="4" bgColor={"#EEEEF9"}>
          <Flex justifyContent="space-between" alignItems="center">
            {loading ? (
              <Spinner size="md" />
            ) : (
              <Image
                src={logoUrl || defaultLogoUrl}
                alt="Logo"
                maxH="38px"
                objectFit="contain"
              />
            )}
            <Image
              src={ArrowLeftIcon}
              alt="Arrow Left Icon"
              height="30px"
              onClick={onClose}
            />
          </Flex>
        </DrawerHeader>

        <DrawerBody p="0" mx={"12px"} overflowY="auto">
          <Flex
            flexDirection={"column"}
            justifyContent={"space-between"}
            minHeight={"100%"}
          >
            {/* Navigation Menu Items */}
            <Box p="8px">
              {sidebar?.map((menu: any, index: number) => {
                const isActive = isRouteExist(pathname, menu?.routes);
                const IconComponent = menu?.iconComponent;

                return (
                  <Flex
                    key={index}
                    alignItems="center"
                    gap="12px"
                    p="12px 16px"
                    borderRadius="8px"
                    cursor="pointer"
                    bg={isActive ? "#1819b4" : "transparent"}
                    color={isActive ? "white" : "#0D0D0D"}
                    onClick={() => handleMenuClick(menu)}
                    _hover={{
                      bg: "#1819b4",
                      color: "white",
                      textColor: "white",
                    }}
                    mb="4px"
                  >
                    {IconComponent && (
                      <Box
                        boxSize={4}
                        color={isActive ? "white" : "#798692"}
                      >
                        <IconComponent />
                      </Box>
                    )}
                    <Text
                      fontSize="16px"
                      fontWeight={isActive ? 500 : 400}
                      color={isActive ? "white" : "#0D0D0D"}
                    >
                      {menu?.text}
                    </Text>
                  </Flex>
                );
              })}
            </Box>

            {/* Auth Buttons Section */}
            <Box p="16px" borderTop="1px solid #E6E9EF" mt="auto">
              <Flex flexDirection="column" gap="12px">
                <CustomButton
                  text={t("unauthenticated.pledgeHeader")}
                  borderRadius={"8px"}
                  border={"1px solid #1819B4"}
                  width="100%"
                  fontSize="14px"
                  color="#1819B4"
                  fontWeight={500}
                  height={"40px"}
                  onClick={() => handleNavigate(ROUTE_NAMES.PLEDGE_LOGIN)}
                  background="transparent"
                  _hover={{ background: "transparent" }}
                />
                <CustomButton
                  text={t("unauthenticated.signup")}
                  borderRadius={"8px"}
                  border={"1px solid #1819B4"}
                  width="100%"
                  fontSize="14px"
                  color="#1819B4"
                  fontWeight={500}
                  height={"40px"}
                  onClick={() => handleNavigate(ROUTE_NAMES.SINGUP)}
                  background="transparent"
                  _hover={{ background: "transparent" }}
                />
                <CustomButton
                  text={t("unauthenticated.login")}
                  borderRadius={"8px"}
                  border={"1px solid #1819B4"}
                  width="100%"
                  fontSize="14px"
                  fontWeight={500}
                  height={"40px"}
                  onClick={() => handleNavigate(ROUTE_NAMES.LOGIN)}
                  background="#1819B4"
                  _hover={{ background: "#1819B4" }}
                />
              </Flex>
            </Box>
          </Flex>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
